/**
 * CrossPostButton — admin-only inline widget for the flagship stories
 * feed. Renders a small popover with the sibling sites; clicking one
 * calls ``POST /api/admin/stories/{id}/cross-post?site_source=<slug>``
 * and shows a toast with the clone id.
 *
 * The parent (Stories page) hides the widget for non-admins.
 */
import { useState } from "react";
import { toast } from "sonner";
import { Share2, Loader2, Check } from "lucide-react";
import { http } from "@/lib/api";

const TARGETS = [
  { value: "kerala",    label: "Kerala Pulse" },
  { value: "foodpulse", label: "Food Pulse" },
  { value: "indias",    label: "India's Projects" },
];

export function CrossPostButton({ storyId, currentSiteSource, className = "" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null); // slug currently posting, or null
  const [done, setDone] = useState({});   // { slug: true } after a successful clone

  const post = async (slug) => {
    setBusy(slug);
    try {
      const { data } = await http.post(
        `/admin/stories/${storyId}/cross-post`,
        null,
        { params: { site_source: slug } }
      );
      setDone((d) => ({ ...d, [slug]: true }));
      toast.success(`Cross-posted to ${TARGETS.find((t) => t.value === slug)?.label}.`, {
        description: `Clone id: ${data.clone_id.slice(0, 8)}…`,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cross-post failed. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  // Never offer to cross-post a story back to its own tenancy.
  const options = TARGETS.filter((t) => t.value !== (currentSiteSource || "main"));
  if (!options.length) return null;

  return (
    <div className={`relative inline-block ${className}`} data-testid={`crosspost-${storyId}`}>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((v) => !v); }}
        className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-full border hover:bg-black/5 transition-colors"
        style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text-soft)" }}
        data-testid={`crosspost-toggle-${storyId}`}
      >
        <Share2 className="w-3 h-3" /> Cross-post
      </button>
      {open && (
        <div
          className="absolute right-0 z-20 mt-1 w-56 rounded-lg border bg-white shadow-lg overflow-hidden"
          style={{ borderColor: "var(--wofy-border)" }}
          onClick={(e) => e.stopPropagation()}
          data-testid={`crosspost-menu-${storyId}`}
        >
          <div className="px-3 py-2 text-[10px] font-mono uppercase tracking-wider border-b" style={{ color: "var(--wofy-text-soft)", borderColor: "var(--wofy-border)" }}>
            Publish this story to…
          </div>
          {options.map((t) => (
            <button
              key={t.value}
              type="button"
              disabled={busy || done[t.value]}
              onClick={(e) => { e.preventDefault(); post(t.value); }}
              className="w-full flex items-center justify-between px-3 py-2 text-sm text-left hover:bg-black/5 disabled:opacity-60"
              data-testid={`crosspost-target-${t.value}-${storyId}`}
            >
              <span>{t.label}</span>
              {busy === t.value ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : done[t.value] ? <Check className="w-3.5 h-3.5" style={{ color: "var(--wofy-primary)" }} />
                : null}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
